import { Box, useTheme } from '@mui/material';
import type {
  ContextVariables,
  ConversationOauthSignInWidgetComponent,
} from 'api/types';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';
import { useUpdateConversation } from 'components/App/hooks';
import { usePoll } from 'components/App/hooks/usePoll';
import { EventMessage } from 'components/EventMessage/EventMessage';
import { OrgThemedButton } from 'components/OrgThemedButton';
import { useAppSelector } from 'hooks';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import React from 'react';
import { selectLegibleTextColorOnLegibleElementColor } from 'reducers/config';
import {
  selectConversationId,
  selectIsAuthorIconVisible,
} from 'reducers/workflows';
import ApiService from 'services/ApiService';

interface ConversationOauthSignInProps {
  areLoadingDotsVisible: boolean;
  isLastStep: boolean;
  setStepContextVariables: (contextVariables: ContextVariables) => void;
  setStepSent: (query?: string) => void;
  stepIndex: number;
  widgetComponent: ConversationOauthSignInWidgetComponent;
}

const TEN_MINUTES = 10 * 60 * 1000;
const FIVE_SECONDS = 5 * 1000;

export const ConversationOauthSignIn = ({
  isLastStep,
  setStepContextVariables,
  setStepSent,
  stepIndex,
  widgetComponent,
}: ConversationOauthSignInProps) => {
  const theme = useTheme();
  const updateConversation = useUpdateConversation();
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );
  const legibleTextColor = useAppSelector(
    selectLegibleTextColorOnLegibleElementColor,
  );
  const conversationId = useAppSelector(selectConversationId);
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);
  const [shouldPoll, setShouldPoll] = React.useState(true); // default to true

  const disabled =
    // disable if any components come after this step
    !isLastStep ||
    // disable if not the primary tab
    !isCurrentTabPrimary ||
    // isSent happens when server reports success or error
    !!widgetComponent.isSent;

  /**
   * logic allows for user to click sign in button again after
   * 10 minutes of inactivity. If the user clicks the sign in button after timeout,
   * we start polling the server again to check if the OAuth credential is active.
   */
  const stopPolling = disabled || !shouldPoll;

  const isConversationOauthCredentialActive = React.useCallback(async () => {
    const response = await ApiService.request(
      'conversation-oauth-credential-status',
      {
        urlValues: {
          conversationId: conversationId,
          conversationOauthCredentialId:
            widgetComponent.component_fields.conversation_oauth_credential_id,
        },
      },
    );

    switch (response.status) {
      case 'active':
        setStepContextVariables({ status: 'active' });
        updateConversation({});
        setStepSent();
        setShouldPoll(false);
        break;
      case 'error':
        setStepContextVariables({ status: 'error' });
        setStepSent();
        setShouldPoll(false);
        break;
    }
  }, [
    conversationId,
    setStepContextVariables,
    setStepSent,
    updateConversation,
    widgetComponent.component_fields.conversation_oauth_credential_id,
  ]);

  const onTimeout = React.useCallback(() => {
    setShouldPoll(false);
  }, []);

  usePoll({
    callback: isConversationOauthCredentialActive,
    duration: TEN_MINUTES,
    initialCall: false,
    interval: FIVE_SECONDS,
    onTimeout,
    shouldStop: stopPolling,
  });

  if (widgetComponent.contextVariables?.status === 'active') {
    return (
      <EventMessage
        widgetComponent={{
          component_fields: {
            message: widgetComponent.component_fields.success_message,
          },
          component_type: 'event_message',
        }}
      />
    );
  }

  if (widgetComponent.contextVariables?.status === 'error') {
    return (
      <EventMessage
        widgetComponent={{
          component_fields: {
            message: widgetComponent.component_fields.error_message,
          },
          component_type: 'event_message',
        }}
      />
    );
  }

  return (
    <AgathaMessage
      isAuthorIconVisible={isAuthorIconVisible}
      stepIndex={stepIndex}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'inline-block',
            height: 0,
            margin: 0,
            padding: 0,
            visibility: 'hidden',
          }}
        >
          {widgetComponent.component_fields.prompt}
        </Box>
        <OrgThemedButton
          component='a'
          disabled={disabled}
          href={widgetComponent.component_fields.auth_uri}
          onClick={() => {
            setShouldPoll(true);
          }}
          rel='noreferrer'
          sx={{
            '&.Mui-disabled': {
              backgroundColor: theme.palette.colors.grey[200],
            },
            color: `${legibleTextColor} !important`,
            minWidth: '100%',
          }}
          target='_blank'
          variant='contained'
        >
          {widgetComponent.component_fields.sign_in_button_label}
        </OrgThemedButton>
      </Box>
    </AgathaMessage>
  );
};
